import "./index.scss"
import React, { useCallback, useEffect, useState, useMemo } from "react"
import { Box, Container, Tab, Tabs } from "@mui/material"
import { POOLS_MAP, PoolName } from "src/constants"
import Text from "src/components/Text"
import Button from "src/components/Button"
import RocketIcon from "src/assets/icons/rocket-black.svg"
import { useTranslation } from "react-i18next"
import CustomCard from "src/components/CustomCard"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import Deposit from "./Deposit"
import Withdraw from "./Withdraw"
import { useNavigate } from "react-router-dom"
import Stake from "./Stake"
import { ChevronLeft } from "@mui/icons-material"
import usePoolData from "src/hooks/usePoolData"
import PoolInfoCard from "src/components/PoolInfoCard"
import BoostedTag from "src/components/BoostFarming/BoostedTag"
import BoostedInfo from "src/components/BoostFarming/BoostedInfo"
import useFarmBoostHandler from "src/hooks/BoostFarming/useFarmBoostHandler"
import { useGetBoostedFarms } from "src/hooks/BoostFarming/useFarmBoostContract"
import { MAX_POOL_SELECTABLE } from "src/constants/boostFarm"
import { getChainId } from "src/utils"

interface Props {
  poolName: PoolName
}

const PoolDetail = ({ poolName }: Props) => {
  const { t } = useTranslation()
  const [tabIndex, setTabIndex] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const chainId = getChainId()
  const pool = POOLS_MAP[poolName]
  const baseRoute = `/pools/${pool.route}`
  const [poolData, userShareData, boostData] = usePoolData(poolName)
  const { data: boostedPoolIds } = useGetBoostedFarms()
  const { onBoostFarm } = useFarmBoostHandler()

  const canBoost = useMemo(() => {
    return (
      userShareData?.stakedAmount.gt(0) &&
      !boostData?.isBoosted &&
      boostedPoolIds?.length !== undefined &&
      boostedPoolIds?.length < MAX_POOL_SELECTABLE
    )
  }, [
    boostData?.isBoosted,
    boostedPoolIds?.length,
    userShareData?.stakedAmount,
  ])

  const handleBoost = useCallback(() => {
    const pid = pool.rewardPids[chainId]!
    void onBoostFarm([pid])
  }, [chainId, onBoostFarm, pool.rewardPids])

  const handleItemClick = (value: number) => {
    setTabIndex(value)
    if (value === 0) {
      navigate("deposit")
    } else if (value === 1) {
      navigate("withdraw")
    } else {
      navigate("stake")
    }
  }

  const goBack = useCallback(() => {
    navigate("/pools")
  }, [navigate])

  useEffect(() => {
    if (location.pathname === `${baseRoute}/deposit`) {
      setTabIndex(0)
    } else if (location.pathname === `${baseRoute}/withdraw`) {
      setTabIndex(1)
    } else if (location.pathname === `${baseRoute}/stake`) {
      setTabIndex(2)
    }
  }, [baseRoute, location])

  return (
    <Container className="pages-pool-detail">
      <Box
        my={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        maxWidth="564px"
        mx="auto"
      >
        <ChevronLeft
          fontSize="large"
          onClick={goBack}
          sx={{ cursor: "pointer" }}
        />
        <Text variant="h2">{t("Pools")}</Text>
        <ChevronLeft fontSize="large" sx={{ opacity: 0 }} />
      </Box>
      <CustomCard
        variant="darkest-gray"
        withGradient
        maxWidth="564px"
        mx="auto"
        mb={3}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <img src={pool.lpToken.icon} height={24} />
            <Text variant="h4" ml={1}>
              {pool.name}
            </Text>
          </Box>
          {boostData?.isBoosted && <BoostedTag />}
          {canBoost && (
            <Button size="small" onClick={handleBoost}>
              <img
                src={RocketIcon}
                height={12}
                style={{ filter: "invert(1)" }}
              />
              <Text bold size="micro" ml={1}>
                Boost
              </Text>
            </Button>
          )}
        </Box>
        {boostData?.isBoosted && (
          <BoostedInfo poolData={poolData} boostData={boostData} />
        )}
        <Tabs
          value={tabIndex}
          onChange={(e, value: number) => handleItemClick(value)}
          centered
        >
          <Tab label={t("Add Liquidity")} value={0} />
          <Tab label={t("Remove Liquidity")} value={1} />
          <Tab label={t("Stake Liquidity")} value={2} />
        </Tabs>
        <Box mt={3}>
          <Routes>
            <Route
              path="/deposit"
              element={
                <Deposit
                  poolName={poolName}
                  poolData={poolData}
                  userShareData={userShareData}
                />
              }
            />
            <Route
              path="/withdraw"
              element={
                <Withdraw
                  poolName={poolName}
                  poolData={poolData}
                  userShareData={userShareData}
                />
              }
            />
            <Route path="/stake" element={<Stake poolName={poolName} />} />
            <Route
              path="*"
              element={<Navigate to={`${baseRoute}/deposit`} replace />}
            />
          </Routes>
        </Box>
      </CustomCard>
      <PoolInfoCard
        poolData={poolData}
        userData={userShareData}
        poolName={poolName}
      />
      <Box height="20px" />
    </Container>
  )
}

export default PoolDetail
